import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, User } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';

/**
 * Non-blocking profile completion banner for progressive profiling
 * Shows on dashboard when profile is incomplete but doesn't block access
 */
export const ProfileCompletionBanner: React.FC = () => {
  const { profile, loading } = useProfile();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  // Don't show if loading, already complete, or dismissed
  if (loading || !profile || profile.profile_completed || dismissed) {
    return null;
  }

  // Check if user has minimal fields (first_name + email)
  const hasMinimalProfile = profile.first_name && profile.email;
  if (!hasMinimalProfile) {
    // This shouldn't happen with new signup flow, but just in case
    return null;
  }

  const handleComplete = () => {
    navigate('/profile');
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Optionally persist dismissal in localStorage
    localStorage.setItem('profile-banner-dismissed', 'true');
  };

  return (
    <div className="bg-gradient-to-r from-cyan-50 to-indigo-50 border border-cyan-200 rounded-2xl p-4 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="p-2 bg-white rounded-full">
          <User className="w-5 h-5 text-cyan-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">
            Complete your profile to unlock personalized examples
          </h3>
          <p className="text-sm text-slate-600 mb-3">
            Add your organization details to get AI responses tailored to your nonprofit context.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={handleComplete}
              className="text-sm font-medium text-cyan-700 hover:text-cyan-800 underline underline-offset-2"
            >
              Complete Profile
            </button>
            <button
              onClick={handleDismiss}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Maybe Later
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="p-1 hover:bg-white rounded-full transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-slate-400" />
        </button>
      </div>
    </div>
  );
};
